import type { PeriodMetrics } from '@store/models';

/** Direction of a period-over-period delta for a single metric. */
export type MetricDeltaDirection = 'up' | 'down' | 'flat';

/**
 * Display-only delta between two periods for one metric. Computed client-side
 * for the two-period comparison view (US3); the underlying values are always
 * server-computed.
 */
export interface MetricDelta {
  /** Absolute change (second period minus first period). */
  value: number;
  /** Direction of the change relative to the first period. */
  direction: MetricDeltaDirection;
}

/**
 * The set of numeric metric keys of {@link PeriodMetrics} that support a
 * period-over-period comparison delta.
 */
export type ComparableMetricKey = Extract<
  keyof PeriodMetrics,
  | 'requestedMeetings'
  | 'completedMeetings'
  | 'requestedAttendees'
  | 'completedAttendees'
  | 'meetingUtilizationRate'
  | 'utilizationHoursPercentage'
>;

/**
 * Computes a display-only delta for a metric between a base and a comparison
 * period value.
 */
export function computeMetricDelta(baseValue: number, comparisonValue: number): MetricDelta {
  const value = comparisonValue - baseValue;
  const direction: MetricDeltaDirection = value > 0 ? 'up' : value < 0 ? 'down' : 'flat';
  return { value, direction };
}
